import { setUserMetadata } from '@/actions/user'
import { AdaptiveModal } from '@/app/components'
import SurveyObserver, {
  useSurveyResponder,
} from '@/app/components/SurveyObserver'
import { useNavigation } from '@/app/hooks'
import {
  Button,
  Card,
  CardAction,
  CardActions,
  LoadingAnimation,
  Text,
} from '@/components'
import TextFormField from '@/components/forms/text-form-field'
import config from '@/config'
import { useAppDispatch, useUrlSearchParam } from '@/hooks'
import { useFormik } from 'formik'
import { ComponentProps, useState } from 'react'
import type { UseFormRegister } from 'react-hook-form'
import * as Sentry from '@sentry/react'
import * as yup from 'yup'

type Props = ComponentProps<typeof AdaptiveModal> & {
  afterLogin?: (res: Response) => Promise<void>
  redirectUrl?: string | undefined
  email?: string
}

const emailValidationSchema = yup.object({
  username: yup.string().email().required().label('Email'),
  password: yup.string().required().label('Password'),
})

function useViewModel(props: Props) {
  const navigation = useNavigation()
  const dispatch = useAppDispatch()
  const emailFromUrl = useUrlSearchParam('email')
  const { submitResponse } = useSurveyResponder('AutogridExistingUser')
  const [submitIsLoading, setSubmitIsLoading] = useState(false)

  const initialEmail = props.email ?? emailFromUrl ?? undefined

  const formik = useFormik({
    initialValues: {
      username: initialEmail ?? '',
      password: '',
    },
    validationSchema: emailValidationSchema,
    onSubmit: async (value, formActions) => {
      setSubmitIsLoading(true)
      const callback = props.afterLogin
      const redirectUrl = props.redirectUrl

      const res = await fetch(`${config.apiGateway.URL}/api/token/`, {
        method: 'POST',
        body: JSON.stringify(value),
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (res.ok) {
        if (!callback) {
          const data = (await res.json()) as { access: string; refresh: string }
          submitResponse({ email: value.username })
          if (!data.access) {
            Sentry.captureMessage('No access token in response', {
              user: { username: value.username },
              level: 'error',
            })
            formActions.setErrors({
              password: 'There was an error, please try again later',
            })
            return
          }
          try {
            const confirmProfileResponse = await fetch(
              `${config.apiGateway.URL}/api/autogrid/confirm_profile_creation`,
              {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${data.access}`,
                  'Content-Type': 'application/json',
                },
              },
            )
            if (!confirmProfileResponse.ok) {
              Sentry.captureMessage(
                'Failed to confirm profile creation for autogrid',
                {
                  user: { username: value.username },
                  level: 'error',
                },
              )
              formActions.setErrors({
                password: 'There was an error, please try again later',
              })
            } else {
              navigation.push(
                redirectUrl ?? '/autogrid/confirmation/verified-existing-user',
              )
            }
          } catch {
            Sentry.captureMessage(
              'Error when trying to create profile for autogrid',
              {
                user: { username: value.username },
                level: 'error',
              },
            )
            formActions.setErrors({
              password: 'There was an error, please try again later',
            })
          }
        } else {
          dispatch(setUserMetadata({ existingAutogridUser: true }))
          await callback?.(res)
        }
      } else if (res.status === 401) {
        formActions.setErrors({
          password: 'The email or password you entered is incorrect.',
        })
      }
      setSubmitIsLoading(false)
    },
  })

  const register = ((name: string) => ({
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    name,
    ref: (_element: HTMLInputElement | null) => {},
  })) as unknown as UseFormRegister<{ username: string; password: string }>

  const inputValues = formik.values
  const inputErrors = {
    username: formik.touched.username ? formik.errors.username : undefined,
    password: formik.touched.password ? formik.errors.password : undefined,
  }

  const canSubmit =
    !submitIsLoading &&
    !formik.errors.username &&
    !formik.errors.password &&
    Boolean(inputValues.username?.trim()) &&
    Boolean(inputValues.password)

  return {
    inputValues,
    inputErrors,
    onSubmit: formik.handleSubmit,
    submitIsLoading,
    canSubmit,
    modalProps: props,
    register,
  }
}

export default function ExistingUserModalSurvey(props: Props) {
  return (
    <SurveyObserver observedSurveys={['AutogridExistingUser']}>
      <ExistingUserModal {...props} />
    </SurveyObserver>
  )
}

export function ExistingUserModal(props: Props) {
  const {
    inputValues,
    inputErrors,
    onSubmit,
    submitIsLoading,
    canSubmit,
    modalProps,
    register,
  } = useViewModel(props)

  return (
    <AdaptiveModal {...modalProps} id="autogrid-existing-user">
      <form
        noValidate
        autoComplete="off"
        style={{ height: '100%' }}
        onSubmit={onSubmit}
      >
        <Card className="!p-0 flex flex-col !h-full" fullHeight flat>
          <Text variant="h2" className="mb-1">
            Welcome back!
          </Text>
          <Text variant="body4" className="mb-6">
            Log in to continue your application.
          </Text>
          <div className="flex flex-col gap-6 flex-1 min-h-0">
            <div className="flex flex-col gap-6 flex-shrink-0">
              <TextFormField
                name="username"
                label="Email"
                type="email"
                placeholder="Enter email"
                autoComplete="do-not-autocomplete"
                register={register}
                value={inputValues.username}
                error={inputErrors.username}
                showErrorDropdown={Boolean(inputErrors.username)}
                disabled={submitIsLoading}
              />
              <TextFormField
                name="password"
                label="Password"
                type="password"
                placeholder="Enter password"
                autoComplete="new-password"
                register={register}
                value={inputValues.password}
                error={inputErrors.password}
                showErrorDropdown={Boolean(inputErrors.password)}
                disabled={submitIsLoading}
              />
              <a
                href="/reset-password"
                target="_blank"
                rel="noreferrer"
                className="cursor-pointer self-start ml-4 underline font-bold text-blue-600 text-[12px] font-[Montserrat]"
              >
                Forgot password?
              </a>
            </div>
            <div className="flex-1 flex justify-center items-center min-h-0">
              {submitIsLoading && (
                <LoadingAnimation type="falling" size="small" />
              )}
            </div>
            <CardActions className="!mt-auto flex-shrink-0">
              <CardAction type="primary">
                <Button
                  type="submit"
                  id="autogrid-existing-user-sign-in-button"
                  className="!mt-0"
                  variant="primary"
                  disabled={!canSubmit}
                >
                  Login
                </Button>
              </CardAction>
            </CardActions>
          </div>
        </Card>
      </form>
    </AdaptiveModal>
  )
}
